import { motion } from 'framer-motion';
import { useInView } from 'framer-motion';
import { useRef } from 'react';
import { Instagram } from 'lucide-react';

import { teamLeaders, teamMembers } from '@/data/team';
import kerimArno from '@/assets/team/arno-kerim-portrait.png.asset.json';
import teamIntroduction from '@/assets/team/team-introduction.mp4.asset.json';
import teamIntroductionPoster from '@/assets/team/team-introduction-poster.jpg.asset.json';

export const Team = () => {
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, margin: "-100px" });

  return (
    <section className="section" ref={ref} id="team">
      <div className="container mx-auto">
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={isInView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.6 }}
          className="text-center mb-12"
        >
          <h2 className="text-3xl md:text-4xl lg:text-5xl font-bold mb-4">
            Who We Are
          </h2>
          <p className="text-muted-foreground text-lg max-w-2xl mx-auto">
            Kerim and Arno — two 25-year-old entrepreneurs with nearly 10 years of marketing experience
          </p>
        </motion.div>

        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 max-w-3xl mx-auto mb-16">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={isInView ? { opacity: 1, y: 0 } : {}}
            transition={{ duration: 0.6 }}
            className="rounded-2xl overflow-hidden border border-white/10 shadow-2xl aspect-[9/16]"
          >
            <img src={kerimArno.url} alt="Arno Adornier and Kerim Jakupovic" className="w-full h-full object-cover" loading="lazy" />
          </motion.div>
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={isInView ? { opacity: 1, y: 0 } : {}}
            transition={{ duration: 0.6, delay: 0.1 }}
            className="rounded-2xl overflow-hidden border border-white/10 shadow-2xl aspect-[9/16] bg-background"
          >
            <video
              controls
              playsInline
              preload="metadata"
              poster={teamIntroductionPoster.url}
              className="w-full h-full object-cover"
              aria-label="Meet the Alpha Omega Hub team"
            >
              <source src={teamIntroduction.url} type="video/mp4" />
              Your browser does not support embedded videos.
            </video>
          </motion.div>
        </div>
        
        {/* Leadership Team */}
        <div className="grid md:grid-cols-2 gap-6 max-w-4xl mx-auto mb-8">
          {teamLeaders.map((member, index) => (
            <motion.div
              key={member.name}
              initial={{ opacity: 0, y: 30 }}
              animate={isInView ? { opacity: 1, y: 0 } : {}}
              transition={{ duration: 0.6, delay: index * 0.1 }}
              className="glass-card text-center group hover:bg-white/10 transition-all duration-500"
            >
              <div className="w-24 h-24 rounded-full mx-auto mb-4 overflow-hidden border-2 border-primary/30">
                <img 
                  src={member.image} 
                  alt={member.name}
                  className="w-full h-full object-cover"
                />
              </div>
              <h3 className="text-xl font-semibold mb-1">{member.name}</h3>
              <p className="text-sm text-primary mb-2">({member.role})</p>
              {member.instagram && (
                <a
                  href={member.instagram}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center gap-1.5 text-xs text-pink-400 hover:text-pink-300 mb-2"
                >
                  <Instagram className="w-3.5 h-3.5" />
                  {member.instagramHandle}
                </a>
              )}
              <p className="text-sm text-muted-foreground mb-3">{member.description}</p>
              <p className="text-xs font-semibold text-primary mb-4">{member.highlight}</p>
              <a
                href={member.contactUrl}
                className="inline-flex items-center gap-2 px-5 py-2 rounded-full border-2 border-primary text-primary text-sm font-semibold hover:bg-primary/10 transition-colors"
              >
                View Contact Info
              </a>
            </motion.div>
          ))}
        </div>
        
        {/* Team Members */}
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6 max-w-5xl mx-auto">
          {teamMembers.map((member, index) => (
            <motion.div
              key={member.name}
              initial={{ opacity: 0, y: 30 }}
              animate={isInView ? { opacity: 1, y: 0 } : {}}
              transition={{ duration: 0.6, delay: (index + 2) * 0.1 }}
              className="glass-card text-center group hover:bg-white/10 transition-all duration-500"
            >
              <div className="w-20 h-20 rounded-full mx-auto mb-4 overflow-hidden border-2 border-primary/20">
                <img 
                  src={member.image} 
                  alt={member.name}
                  className="w-full h-full object-cover"
                />
              </div>
              <h3 className="text-lg font-semibold mb-1">{member.name}</h3>
              <p className="text-sm text-primary/80 mb-2">({member.role})</p>
              <p className="text-xs text-muted-foreground mb-2">{member.description}</p>
              {member.highlight && (
                <p className="text-xs font-semibold text-primary/80">{member.highlight}</p>
              )}
            </motion.div>
          ))}
        </div>
        
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={isInView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.6, delay: 0.6 }}
          className="text-center mt-12"
        >
          <a href="/#contact" className="btn-hero-primary">
            Meet the Team
          </a>
        </motion.div>
      </div>
    </section>
  );
};
